import { createEffect, createSignal, onCleanup, untrack } from "solid-js";

/**
 * Rete di sicurezza per la conferma "a vuoto" (Applica con valori identici:
 * nessuna chiave nuova, nessun fetch che nasca). NON regge la correttezza: la
 * finestra resta aperta finché c'è lavoro osservabile — un fetch in volo o una
 * chiave confermata che la query non ha ancora adottato — e se la rete scatta
 * lo stesso troppo presto, il fetch che nasce dopo riaccende il segnale. Il
 * fallimento degrada in un ritardo, mai in un'assenza definitiva.
 */
export const CONFIRM_SETTLE_WINDOW_MS = 700;

type FetchStatus = "fetching" | "paused" | "idle";

export type SearchActivity = {
  /**
   * Stato del SOLO fetch di ricerca. La paginazione non passa di qui (AC12):
   * chi chiama maschera `fetchNextPage` a "idle", così "Carica altri" non può
   * né accendere né spegnere il segnale.
   */
  fetchStatus: FetchStatus;
  /** Chiave dei filtri confermati nell'URL, pre-debounce. */
  pendingKey: string;
  /** Chiave dei filtri su cui la query è davvero montata. */
  appliedKey: string;
};

/**
 * `paused` significa "ancora in volo": con `networkMode: "online"` (default
 * TanStack, mai sovrascritto qui) una query senza rete non fallisce, resta
 * sospesa finché la rete non torna. Una sequenza fetching → paused → fetching
 * non è quindi né una nascita né un assestamento.
 */
const isFlying = (fetchStatus: FetchStatus) => fetchStatus !== "idle";

/**
 * C'è lavoro osservabile: un fetch in volo, oppure una chiave confermata che
 * la query non ha ancora adottato — in quel caso il fetch arriverà per
 * costruzione (il debounce URL→filtri è ancora in corso), per quanto lenta sia
 * la catena.
 */
const hasWork = (activity: SearchActivity) =>
  isFlying(activity.fetchStatus) || activity.pendingKey !== activity.appliedKey;

const snapshot = (activity: SearchActivity) => ({
  flying: isFlying(activity.fetchStatus),
  work: hasWork(activity),
});

/**
 * Il segnale di "ricerca in corso per volontà dell'utente" (AC8–AC10, D8):
 * si accende alla CONFERMA (Applica / Reset / retry con lista conservata),
 * pre-debounce — mai derivato da `isFetching` (E-8: nessun lampeggio tra due
 * fetch ravvicinati, niente segnale per "Carica altri" o refetch invisibili).
 *
 * Lo spegnimento è legato all'IDENTITÀ del fetch: solo un fetch NATO dopo la
 * conferma è quello dell'utente. Quello già in volo al momento del clic non
 * viene adottato — il suo assestamento (che riguarda la ricerca precedente)
 * non spegne nulla, altrimenti la lista tornerebbe a piena opacità su
 * risultati già superati e la ricerca vera resterebbe invisibile.
 *
 * L'episodio di conferma resta armato finché il fetch adottato non si assesta:
 * la rete di sicurezza può spegnere la vista, mai l'episodio, quindi un
 * ritardo produce al più un ritardo.
 */
export function useUserSearchInProgress(getActivity: () => SearchActivity) {
  const [isInProgress, setIsInProgress] = createSignal(false);

  /** Una conferma dell'utente è in sospeso (non ancora risolta da un fetch). */
  let armed = false;
  /** Un fetch nato dopo la conferma è stato riconosciuto come "quello". */
  let adopted = false;
  let safetyTimer: ReturnType<typeof setTimeout> | undefined;

  const cancelSafety = () => {
    clearTimeout(safetyTimer);
    safetyTimer = undefined;
  };

  const armSafety = () => {
    cancelSafety();
    safetyTimer = setTimeout(() => {
      safetyTimer = undefined;
      // Rilettura allo scatto: se nel frattempo è comparso del lavoro la rete
      // non tocca niente e lascia decidere all'assestamento.
      if (armed && !untrack(() => hasWork(getActivity()))) {
        setIsInProgress(false);
      }
    }, CONFIRM_SETTLE_WINDOW_MS);
  };

  /** Il fetch dell'utente si è assestato: episodio chiuso. */
  const resolve = () => {
    armed = false;
    adopted = false;
    cancelSafety();
    setIsInProgress(false);
  };

  const confirm = () => {
    armed = true;
    // Ciò che è già in volo appartiene alla ricerca precedente: si riparte da
    // zero e si aspetta una nascita.
    adopted = false;
    setIsInProgress(true);
    armSafety();
  };

  createEffect<{ flying: boolean; work: boolean }>(
    (prev) => {
      const activity = getActivity();
      const next = snapshot(activity);
      if (!armed) return next;

      if (next.flying && !prev.flying) {
        // Nascita dopo la conferma: è il fetch dell'utente. Il `setIsInProgress`
        // non è un'accensione spontanea — l'episodio è già armato dalla
        // conferma — ma la riaccensione dopo una rete scattata troppo presto.
        adopted = true;
        cancelSafety();
        setIsInProgress(true);
        return next;
      }
      if (next.work) {
        cancelSafety();
        return next;
      }
      // Da qui in poi non c'è più nulla in volo né in arrivo.
      if (adopted && prev.flying) {
        resolve();
        return next;
      }
      // Il lavoro è finito senza essere nostro (fetch della ricerca
      // precedente, o chiave servita dalla cache): nuova finestra.
      if (prev.work) armSafety();
      return next;
    },
    untrack(() => snapshot(getActivity())),
  );

  onCleanup(cancelSafety);

  return { isInProgress, confirm };
}
